import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import ImageComponent from '../../../components/ImageComponent';
import { canChangeMode, formatBannerTitle } from '../helper';
import type {
  ISavedModeData,
  WithdrawMode,
} from '../../../interfaces/Withdrawal';
import styles from '../styles.module.css';

interface ModeCardProps {
  mode: WithdrawMode;
  data?: ISavedModeData;
  onAdd: (mode: WithdrawMode) => void;
  onChange: (mode: WithdrawMode) => void;
  onBlocked: (message: string) => void;
}

/**
 * One withdrawal mode — either a prompt to add it, or the linked account with
 * its warnings.
 *
 * Mirrors `SavedWithdrawalModesFragment`: no delete action exists, only add and
 * change. A KYC name mismatch surfaces here as `infoBanner`.
 */
const ModeCard: React.FC<ModeCardProps> = ({
  mode,
  data,
  onAdd,
  onChange,
  onBlocked,
}) => {
  const { t } = useTranslation();
  const saved = data?.withdrawalModes;
  const addData = data?.addWithdrawalModeData;

  // Nothing to render for a mode the server did not describe at all.
  if (!saved && !addData) return null;

  /* ---- not linked yet ---- */
  if (!saved) {
    return (
      <button
        type="button"
        className={styles.modeCardAdd}
        onClick={() => onAdd(mode)}
      >
        <span className="fontSizeSm14 fontWeightBold">
          {addData?.title ||
            t(mode === 'BANK' ? 'addBankTitle' : 'addUpiTitle')}
        </span>
        {Boolean(addData?.withdrawalLogos?.length) && (
          <span className={styles.modeLogos}>
            {addData?.withdrawalLogos?.slice(0, 4).map((logo) => (
              <ImageComponent
                key={logo}
                src={logo}
                alt=""
                className={styles.modeLogo}
              />
            ))}
          </span>
        )}
      </button>
    );
  }

  /* ---- linked ---- */
  const bannerTitle = formatBannerTitle(saved.infoBanner);
  const hasMismatch = Boolean(saved.infoBanner);
  const isChangeable = canChangeMode(saved.isKycApproved, saved.allowChange);

  const handleChange = () => {
    if (saved.allowChange) {
      onChange(mode);
      return;
    }
    // KYC not approved: Android shows a nudge here rather than opening the edit
    // form, so surface the server's reason instead of misrouting the user.
    onBlocked(bannerTitle || t('modeChangeUnavailable'));
  };

  return (
    <div className={clsx(styles.modeCard, hasMismatch && styles.modeCardError)}>
      <div className={styles.modeCardHead}>
        {saved.icon && (
          <ImageComponent src={saved.icon} alt="" className={styles.modeIcon} />
        )}
        <div className={styles.modeCardText}>
          <p className={clsx(styles.modeCardTitle, 'fontSizeSm12 fontColor666')}>
            {saved.title}
          </p>
          <p className={clsx(styles.modeCardName, 'fontSizeSm14 fontWeightBold')}>
            {saved.userName}
          </p>
          <p className={clsx(styles.modeCardInfo, 'fontSizeSm12 fontColor666')}>
            {saved.vpaOrbankInfo}
          </p>
        </div>

        {isChangeable && (
          <button
            type="button"
            className={clsx(styles.modeChangeBtn, 'fontSizeSm14 fontWeightBold')}
            onClick={handleChange}
          >
            {t('changeLabel')}
          </button>
        )}
      </div>

      {saved.pendingStateText?.text && (
        <p className={clsx(styles.modePending, 'fontSizeSm12')}>
          {saved.pendingStateText.text}
        </p>
      )}

      {hasMismatch && (
        <div className={styles.modeBanner}>
          <p className={clsx(styles.modeBannerTitle, 'fontSizeSm12 fontWeightBold')}>
            {bannerTitle}
          </p>
          {saved.infoBanner?.desc?.text && (
            <p
              className={clsx(styles.modeBannerDesc, 'fontSizeSm12')}
              // Server sends this description as markup, as Android renders it.
              dangerouslySetInnerHTML={{ __html: saved.infoBanner.desc.text }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ModeCard;
